import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import MarketingLayout from './components/MarketingLayout';
import HomePage from './pages/HomePage';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider } from './contexts/AuthContext';
import { AccessibilityProvider } from './components/Accessibility';
import { I18nProvider } from './contexts/I18nProvider';
import { SidebarProvider } from './contexts/SidebarContext';
import { liquidGlassTheme } from './theme/liquidGlassTheme';
import { useThemeRipple } from './hooks/useTheme';
import ProtectedRoute from './components/auth/ProtectedRoute';

const Layout = lazy(() => import('./components/Layout'));
const ConsultationPage = lazy(() => import('./pages/ConsultationPage'));
const ConsultationInfoPage = lazy(() => import('./pages/ConsultationInfoPage'));
const ConsultationHistoryPage = lazy(() => import('./pages/ConsultationHistoryPage'));
const CaseFilesPage = lazy(() => import('./pages/CaseFilesPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const DocumentsInfoPage = lazy(() => import('./pages/DocumentsInfoPage'));
const ResearchPage = lazy(() => import('./pages/ResearchComingSoonPage'));
const ResearchInfoPage = lazy(() => import('./pages/ResearchComingSoonPage'));
const AiResearchPage = lazy(() => import('./pages/AiResearchPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ContractAnalysisPage = lazy(() => import('./pages/ContractAnalysis'));
const ContractAnalysisInfoPage = lazy(() => import('./pages/ContractAnalysisInfoPage'));
const SharedContractPage = lazy(() => import('./pages/SharedContractPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogDetailPage = lazy(() => import('./pages/BlogDetailPage'));
const ContactsPage = lazy(() => import('./pages/ContactsPage'));
const TermsOfUsePage = lazy(() => import('./pages/TermsOfUsePage'));
const CookiesPage = lazy(() => import('./pages/CookiesPage'));
const GeneralTermsPage = lazy(() => import('./pages/GeneralTermsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const JurisprudenceSearchPage = lazy(() => import('./pages/JurisprudenceSearchPage'));
const SubjectSearchPage = lazy(() => import('./pages/SubjectSearchPage'));
const SubjectSearchInfoPage = lazy(() => import('./pages/SubjectSearchInfoPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/SubscriptionSuccessPage'));
const SubscriptionCancelPage = lazy(() => import('./pages/SubscriptionCancelPage'));
const AccountSettingsPage = lazy(() => import('./pages/AccountSettingsPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

function App() {
  useThemeRipple(false);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
      <ThemeProvider theme={liquidGlassTheme}>
        <CssBaseline />
        <GlobalStyles styles={{
          'html, body': {
            overflowX: 'hidden',
            maxWidth: '100vw',
          },
          '*': {
            boxSizing: 'border-box',
          }
        }} />
        <AccessibilityProvider>
          <AuthProvider>
            <SidebarProvider>
              <Router>
              <ScrollToTop />
              <AppRoutes />
              </Router>
            </SidebarProvider>
          </AuthProvider>
        </AccessibilityProvider>
      </ThemeProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App;

function AppRoutes() {
  const adminEnabled = process.env.REACT_APP_ENABLE_ADMIN === 'true';
  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Login - standalone */}
        <Route path="/login" element={<LoginPage />} />

        {/* Marketing pages - Navbar + Footer, no sidebar */}
        <Route element={<MarketingLayout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="consultation-info" element={<ConsultationInfoPage />} />
          <Route path="documents-info" element={<DocumentsInfoPage />} />
          <Route path="contract-analysis-info" element={<ContractAnalysisInfoPage />} />
          <Route path="research-info" element={<ResearchInfoPage />} />
          <Route path="pricing" element={<SubscriptionPage />} />
          <Route path="cenik" element={<Navigate to="/pricing" replace />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:id" element={<BlogDetailPage />} />
          <Route path="kontakty" element={<ContactsPage />} />
          <Route path="podminky-pouziti" element={<TermsOfUsePage />} />
          <Route path="terms" element={<Navigate to="/podminky-pouziti" replace />} />
          <Route path="cookies" element={<CookiesPage />} />
          <Route path="obchodni-podminky" element={<GeneralTermsPage />} />
          <Route path="privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="shared/:shareId" element={<SharedContractPage />} />
          <Route path="subject-search-info" element={<SubjectSearchInfoPage />} />
        </Route>

        {/* App pages - Sidebar layout (ALL require authentication) */}
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="consultation" element={<ConsultationPage />} />
          <Route path="case-files" element={<CaseFilesPage />} />
          <Route path="consultation-history" element={<ConsultationHistoryPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="contract-analysis" element={<ContractAnalysisPage />} />
          <Route path="research" element={<ResearchPage />} />
          <Route path="ai-research" element={<AiResearchPage />} />
          <Route path="jurisprudence" element={<JurisprudenceSearchPage />} />
          <Route path="subject-search" element={<SubjectSearchPage />} />
          <Route path="pricing/success" element={<SubscriptionSuccessPage />} />
          <Route path="pricing/canceled" element={<SubscriptionCancelPage />} />
          <Route path="settings" element={<AccountSettingsPage />} />
          <Route path="security" element={<Navigate to="/settings" replace />} />
          {adminEnabled ? (
            <Route path="admin" element={<AdminPage />} />
          ) : (
            <Route path="admin" element={<Navigate to="/" replace />} />
          )}
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
